"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, CircleX as XCircle, TrendingUp, ArrowRight, ShieldCheck, Percent, SearchCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
const metrics = [{
  icon: TrendingUp,
  title: "RevPAR growth",
  baseline: "Flat to 3-5% seasonal variance with unmanaged gaps",
  baselineStatus: "negative",
  hotelRev: "14% to 28% measured lift in the first 90 days",
  hotelRevStatus: "positive",
  highlight: "+14-28%"
}, {
  icon: ShieldCheck,
  title: "Rate parity adherence",
  baseline: "60-70% consistency with frequent OTA price leakage",
  baselineStatus: "negative",
  hotelRev: "98%+ channel parity locked with real-time audit triggers",
  hotelRevStatus: "positive",
  highlight: "98%+"
}, {
  icon: Percent,
  title: "OTA commission load",
  baseline: "18-25% standard drain without direct booking defense",
  baselineStatus: "negative",
  hotelRev: "Direct shift reduces effective commission fees by 6-11%",
  hotelRevStatus: "positive",
  highlight: "-6-11%"
}, {
  icon: SearchCheck,
  title: "Rate shopping & market intel",
  baseline: "Manual spot checks and delayed monthly reports",
  baselineStatus: "negative",
  hotelRev: "Daily automated rate shopping with proactive adjustments",
  hotelRevStatus: "positive",
  highlight: "Daily Intel"
}];
export default function Comparison() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="baselinecomparison" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="space-y-12">
          {/* Section Header */}
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <Badge variant="outline" className="border-secondary text-foreground font-mono uppercase tracking-wider text-xs px-3 py-1">
              Performance Benchmark
            </Badge>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              See how your numbers change
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg">
              Compare typical standalone performance with our managed revenue framework.
            </p>
          </div>

          {/* Comparison Table for Desktop */}
          <div className="hidden md:block">
            <Card className="rounded-xl border border-border shadow-md bg-card overflow-hidden">
              <Table>
                <TableHeader className="bg-muted/60">
                  <TableRow className="border-b border-border hover:bg-transparent">
                    <TableHead className="w-1/3 py-5 px-6 font-serif text-base font-semibold text-foreground">
                      Metric
                    </TableHead>
                    <TableHead className="w-1/3 py-5 px-6 font-serif text-base font-semibold text-muted-foreground">
                      Industry Baseline
                    </TableHead>
                    <TableHead className="w-1/3 py-5 px-6 font-serif text-base font-semibold text-primary bg-secondary/15 border-l border-border">
                      <div className="flex items-center justify-between">
                        <span>Results with HotelRevBaBa</span>
                        <Badge variant="secondary" className="bg-secondary text-secondary-foreground text-xs uppercase">
                          Target
                        </Badge>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {metrics.map((item, i) => {
                  const IconComponent = item.icon;
                  return <TableRow key={item.title} data-index={i} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                        <TableCell className="py-6 px-6 align-top">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-md bg-muted text-foreground shrink-0 mt-0.5">
                              <IconComponent className="size-4 text-foreground" />
                            </div>
                            <div>
                              <p className="font-semibold text-foreground text-base">
                                {item.title}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-6 px-6 align-top">
                          <div className="flex items-start gap-2.5">
                            <XCircle className="size-5 text-destructive shrink-0 mt-0.5" />
                            <span className="text-sm text-muted-foreground leading-relaxed">
                              {item.baseline}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="py-6 px-6 align-top bg-secondary/5 border-l border-border">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2.5">
                              <CheckCircle2 className="size-5 text-emerald-600 shrink-0 mt-0.5" />
                              <span className="text-sm font-medium text-foreground leading-relaxed">
                                {item.hotelRev}
                              </span>
                            </div>
                            <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-secondary/20 text-foreground shrink-0">
                              {item.highlight}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>;
                })}
                </TableBody>
              </Table>
            </Card>
          </div>

          {/* Mobile Comparison Cards */}
          <div className="grid grid-cols-1 gap-6 md:hidden">
            {metrics.map((item, i) => {
            const IconComponent = item.icon;
            return <motion.div key={item.title} data-index={i} variants={itemVariants}>
                  <Card className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                    <div className="p-5 border-b border-border bg-muted/40 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-md bg-background border border-border text-foreground">
                          <IconComponent className="size-4" />
                        </div>
                        <h3 className="text-base font-semibold text-foreground font-serif">
                          {item.title}
                        </h3>
                      </div>
                      <Badge variant="secondary" className="bg-secondary text-secondary-foreground text-xs font-mono">
                        {item.highlight}
                      </Badge>
                    </div>

                    <CardContent className="p-5 space-y-4">
                      <div className="p-3.5 rounded-md bg-muted/40 border border-border/60">
                        <div className="flex items-center gap-2 mb-1.5">
                          <XCircle className="size-4 text-destructive shrink-0" />
                          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground font-mono">
                            Industry Baseline
                          </p>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed pl-6">
                          {item.baseline}
                        </p>
                      </div>

                      <div className="p-3.5 rounded-md bg-secondary/10 border border-secondary/30">
                        <div className="flex items-center gap-2 mb-1.5">
                          <CheckCircle2 className="size-4 text-emerald-600 shrink-0" />
                          <p className="text-xs font-semibold uppercase tracking-wider text-foreground font-mono">
                            Results with HotelRevBaBa
                          </p>
                        </div>
                        <p className="text-sm font-medium text-foreground leading-relaxed pl-6">
                          {item.hotelRev}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>

          {/* Bottom Action Box */}
          <motion.div variants={itemVariants} className="rounded-xl border border-border bg-primary text-primary-foreground p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-lg">
            <div className="space-y-2 text-center md:text-left">
              <h3 className="text-2xl font-serif font-bold text-primary-foreground">
                Ready to review your hotel revenue?
              </h3>
              <p className="text-primary-foreground/80 text-sm sm:text-base max-w-xl">
                Get an objective audit of your distribution channels, rate parity, and commission leakages.
              </p>
            </div>
            <div className="shrink-0 w-full md:w-auto">
              <Button asChild size="lg" className="w-full md:w-auto bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold px-8 h-12 shadow-sm rounded-full">
                <Link to="#" className="inline-flex items-center justify-center gap-2">
                  <span>Book a consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}